/**
 * Recovering the human-readable message from a failed `supabase.functions.invoke`.
 *
 * supabase-js collapses every non-2xx into a `FunctionsHttpError` whose `message` is the generic
 * "Edge Function returned a non-2xx status code" — the reason the function actually gave is only in
 * the unread response body hanging off `error.context`. Without this, a superadmin who invites a
 * duplicate address sees a status string instead of "That email already has admin access."
 *
 * Lives outside `lib/supabase.ts` so it can be unit-tested: that module reads `import.meta.env` at
 * load time, which Jest cannot execute, so every suite mocks it wholesale and nothing inside it is
 * ever really exercised.
 */

/** The shape supabase-js attaches to a FunctionsHttpError. Only `json()` is needed. */
interface ResponseLike {
  json?: () => Promise<unknown>;
}

/**
 * @param error - The `error` returned alongside `data` by `functions.invoke`
 * @param fallback - Used when the error carries no message of its own
 * @returns The function's own `error` string when it can be read, else the transport-level message
 */
export async function extractEdgeFunctionError(
  error: unknown,
  fallback = 'Request failed.'
): Promise<string> {
  const transportMessage =
    error instanceof Error && error.message ? error.message : fallback;

  const response = (error as { context?: ResponseLike } | null)?.context;
  if (!response || typeof response.json !== 'function') {
    return transportMessage;
  }

  try {
    const body = (await response.json()) as { error?: unknown } | null;
    const detail = body?.error;
    // Only take a non-empty string: a truthy-but-structured `error` is not presentable as-is.
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  } catch {
    // Body was absent, already consumed, or not JSON — the transport message is all there is.
  }

  return transportMessage;
}
